import {
  Divider,
  GridItem,
  TextInputField,
  Text,
  GhostButton,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Field, useField } from "formik";
import { Trans, useTranslation } from "react-i18next";

import { PreviousName } from "./PreviousName";

type NameCheckField = components["schemas"]["NameCheckField"];

export function NameInputField({ field: config }: { field: NameCheckField }) {
  const { t } = useTranslation();
  const [field, , helpers] = useField<NameCheckField["value"]>(config.name);

  const handleDelete = (index: number) => {
    const currentNames = [...field.value];
    currentNames.splice(index, 1);
    helpers.setValue(currentNames);
  };

  return (
    <>
      <GridItem>
        <Field
          as={TextInputField}
          label={t("Legal first name")}
          name={`${config.name}[0].first_name`}
          isRequired
          testID="personal-info-legal-first-name-input"
        />
      </GridItem>
      <GridItem>
        <Field
          as={TextInputField}
          label={t("Middle name")}
          name={`${config.name}[0].middle_name`}
          optionalText={t("if applicable")}
          description={t(
            "Leave this field empty if you do not have any middle name(s)"
          )}
          testID="personal-info-middle-name-input"
        />
      </GridItem>
      <GridItem>
        <Field
          as={TextInputField}
          label={t("Legal last name")}
          name={`${config.name}[0].last_name`}
          isRequired
          testID="personal-info-legal-last-name-input"
        />
      </GridItem>

      <GridItem>
        <Divider />
      </GridItem>

      <GridItem
        className={css({
          gap: 8,
        })}
      >
        <Text as="h2" style="form/section-heading">
          {t("Previous names")} ({t("if applicable")})
        </Text>
        <Text style="form/body-secondary" color="text-weak">
          <Trans>
            These are previous government-documented names or aliases, such as
            maiden names or other former names.
          </Trans>
        </Text>
      </GridItem>

      {field.value.slice(1).map((_, index: number) => (
        <PreviousName
          key={index}
          index={index + 1}
          field={config}
          onDelete={() => handleDelete(index + 1)}
        />
      ))}

      <GridItem
        className={css({
          display: "block",
        })}
      >
        <GhostButton
          testID="personal-info-add-previous-name-button"
          leftIcon="add"
          size="medium"
          fill="background-base"
          border="border-strong"
          onPress={() => {
            helpers.setValue([
              ...field.value,
              {
                first_name: "",
                middle_name: "",
                last_name: "",
                type: "ALIAS",
              },
            ]);
          }}
        >
          <Trans>Add previous name</Trans>
        </GhostButton>
      </GridItem>
    </>
  );
}
